import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import { FilterItemConfig } from '@/components/ui/data-table-customized'
import { ContractAndService } from '@/modules/Customer/types'
import {
    CONTRACT_DIRECTION_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
    CONTRACT_STATUS,
    CONTRACT_SLA_OPTIONS,
    CONTRACT_NDA_OPTIONS,
    CONTRACT_RENEW_OPTIONS,
} from '@/modules/Contract/constants'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { generatePathDatacentral } from '@/utils/path'

// ============================================================================
// Column Configuration
// ============================================================================

/**
 * Column configuration for Customer Contracts table
 * Based on backend sortable config: contract_id, contract_effective_date,
 * contract_expiry_date, contract_status, contract_value, created_at
 */
export const getContractColumns = (
    t: TFunction
): DataTableColumn<ContractAndService>[] => [
    {
        key: 'contract_number_id',
        title: t('contract_number_id'),
        width: 200,
        sortable: false,
        formatter: createInternalLinkFormatter({
            pathKey: 'contractDetail',
            pathParams: (_, row) => ({ id: String(row.contract_id) }),
            label: (_, row) => row.contract_number_id,
        }),
        pinned: 'left',
    },
    {
        key: 'contract_id',
        title: t('contract_id'),
        width: 100,
        sortable: true,
        formatter: createInternalLinkFormatter({
            pathKey: 'contractDetail',
            pathParams: (value) => ({ id: value }),
        }),
    },
    {
        key: 'service_id',
        title: t('service'),
        width: 400,
        sortable: false,
        render: (_, row) => {
            if (!row.service_id) return '-'

            return (
                <Link
                    to={generatePathDatacentral('/services/:id', {
                        id: row.service_id,
                    })}
                    className="text-primary hover:text-primary-hover hover:underline uppercase"
                >
                    {row.product_group_name} - {row.service_name}
                </Link>
            )
        },
    },
    {
        key: 'contract_effective_date',
        title: t('contract_effective_date'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'contract_expiry_date',
        title: t('contract_expiry_date'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'contract_value',
        title: t('contract_value'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: 'VND',
        }),
    },
    {
        key: 'contract_status',
        title: t('contract_status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({
            options: CONTRACT_STATUS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_role',
        title: t('contract_role'),
        width: 160,
        sortable: false,
        formatter: createOptionsFormatter({
            options: CONTRACT_ROLE_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_direction',
        title: t('contract_direction'),
        width: 160,
        sortable: false,
        formatter: createOptionsFormatter({
            options: CONTRACT_DIRECTION_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'partner_name',
        title: t('partner_name'),
        width: 180,
        sortable: false,
        render: (_, row) => {
            if (!row.partner_id) return '-'
            return (
                <div className="min-w-0 max-w-full leading-normal">
                    <Link
                        to={generatePathDatacentral(
                            '/contracts/partners/suppliers/:partnerID',
                            {
                                partnerID: row.partner_id,
                            }
                        )}
                        className="block max-w-full truncate text-primary hover:text-primary-hover hover:underline"
                        title={row.partner_name || undefined}
                    >
                        {row.partner_name}
                    </Link>
                    {row.partner_code && (
                        <div
                            className="max-w-full truncate text-muted-foreground"
                            title={row.partner_code}
                        >
                            {row.partner_code}
                        </div>
                    )}
                </div>
            )
        },
    },
    {
        key: 'partner_email',
        title: t('partner_email'),
        width: 180,
        sortable: false,
        render: (_, row) => {
            if (!row.partner_id) return '-'
            return (
                <div className="min-w-0 max-w-full leading-normal">
                    {row.partner_email && (
                        <div
                            className="max-w-full truncate"
                            title={row.partner_email}
                        >
                            {row.partner_email}
                        </div>
                    )}
                    {row.partner_phone && (
                        <div
                            className="max-w-full truncate text-muted-foreground"
                            title={row.partner_phone}
                        >
                            {row.partner_phone}
                        </div>
                    )}
                </div>
            )
        },
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 120,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
]

// ============================================================================
// Filter Items Config
// ============================================================================

/**
 * Filter configuration for Customer Contracts
 * Based on API documentation filter fields
 * @param t - Translation function
 */
export const getContractFilterItems = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'contract_status',
        type: 'multi-select',
        label: t('contract_status'),
        placeholder: t('select-status'),
        options: CONTRACT_STATUS(t).map((status) => ({
            value: status.value,
            label: status.label,
        })),
    },
    {
        name: 'contract_role',
        type: 'multi-select',
        label: t('contract_role'),
        placeholder: t('contract_role'),
        options: CONTRACT_ROLE_OPTIONS(t).map((opt) => ({
            value: opt.value,
            label: opt.label,
        })),
    },
    {
        name: 'contract_direction',
        type: 'multi-select',
        label: t('contract_direction'),
        placeholder: t('contract_direction'),
        options: CONTRACT_DIRECTION_OPTIONS(t).map((opt) => ({
            value: opt.value,
            label: opt.label,
        })),
    },
    {
        name: 'sla_enable',
        type: 'multi-select',
        label: t('sla_enable'),
        placeholder: t('select-sla'),
        options: CONTRACT_SLA_OPTIONS(t).map((opt) => ({
            value: String(opt.value),
            label: opt.label,
        })),
    },
    {
        name: 'nda_enable',
        type: 'multi-select',
        label: t('nda_enable'),
        placeholder: t('select-nda'),
        options: CONTRACT_NDA_OPTIONS(t).map((opt) => ({
            value: String(opt.value),
            label: opt.label,
        })),
    },
    {
        name: 'auto_renewable',
        type: 'multi-select',
        label: t('auto_renewable'),
        placeholder: t('select-auto-renewable'),
        options: CONTRACT_RENEW_OPTIONS(t).map((opt) => ({
            value: String(opt.value),
            label: opt.label,
        })),
    },
]
